import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { IVerbaScholaCourse, IVerbaScholaTeacherCourse } from '../api/VerbaScholaApi';
import { VerbaScholaCard, VerbaScholaCardHeader, VerbaScholaEmptyState, verbaScholaInitials } from '../component/VerbaScholaTeacherShell';
import { BLUE, BLUE_TEXT, BORDER, ERROR_SOFT, ERROR_TEXT, INTER, NAVY, POPPINS, primaryButtonSx, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';
import { meetingDaysLabel, normalizeMeetingDays, orderedMeetingDays, startTimeParts, startTimeValue, TimePeriod, WEEKDAY_OPTIONS } from '../util/verbaScholaCourseSchedule';

const EMPTY_TEACHER_FORM = { firstName: '', lastName: '', email: '' };

function statusChipSx(status: string) {
    const positive = status === 'accepted' || status === 'published' || status === 'current' || status === 'owner';
    return { fontFamily: INTER, fontWeight: 600, fontSize: 11, textTransform: 'capitalize', color: positive ? SUCCESS_TEXT : WARNING_TEXT, backgroundColor: positive ? SUCCESS_SOFT : WARNING_SOFT };
}

function StudentSignupCard({ signupUrl, onCopy }: { signupUrl: string | null; onCopy: () => void }) {
    return (
        <VerbaScholaCard>
            <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.75 } }}>
                <Box>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Student signup</Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12.5, lineHeight: 1.5, color: SLATE, mt: 0.5 }}>
                        Share this private link with students joining the class.
                    </Typography>
                </Box>
                <Box sx={{ p: 1.25, borderRadius: '11px', backgroundColor: SURFACE_MUTED, overflow: 'hidden' }}>
                    <Typography noWrap sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>{displaySignupUrl(signupUrl)}</Typography>
                </Box>
                <Button
                    fullWidth
                    startIcon={<ContentCopyRoundedIcon />}
                    disabled={!signupUrl}
                    onClick={onCopy}
                    sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, textTransform: 'none' }}
                >
                    Copy signup link
                </Button>
            </Stack>
        </VerbaScholaCard>
    );
}

const TIME_HOUR_OPTIONS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const TIME_MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

function textValue(value: string | null | undefined): string {
    return value ?? '';
}

function optionalText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}

function displaySignupUrl(url: string | null): string {
    return url?.replace(/^https?:\/\//i, '') ?? 'Signup link is being generated.';
}

function teacherFormFor(row: IVerbaScholaTeacherCourse): typeof EMPTY_TEACHER_FORM {
    return { firstName: row.first_name ?? '', lastName: row.last_name ?? '', email: row.email ?? '' };
}

function teacherStatusLabel(teacher: IVerbaScholaTeacherCourse): string {
    if (teacher.status === 'accepted' && teacher.has_joined === false) return 'Invited';
    if (teacher.status === 'accepted') return 'Active';
    return teacher.status.charAt(0).toUpperCase() + teacher.status.slice(1);
}

function TeacherAvatar({ teacher, isOwner }: { teacher: IVerbaScholaTeacherCourse; isOwner: boolean }) {
    const label = teacher.display_name ?? teacher.email ?? null;
    return (
        <Avatar sx={{ width: 40, height: 40, backgroundColor: isOwner ? NAVY : BLUE, fontFamily: POPPINS, fontSize: 12 }}>
            {verbaScholaInitials(label, teacher.id)}
        </Avatar>
    );
}

function TeacherAccessCards() {
    return (
        <Stack spacing={2.5}>
            <VerbaScholaCard>
                <Stack spacing={1} sx={{ p: { xs: 2, sm: 2.75 } }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Teacher access</Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12.5, lineHeight: 1.55, color: SLATE }}>
                        Invited teachers can edit lessons, manage students, and provide speaking feedback for this class.
                    </Typography>
                </Stack>
            </VerbaScholaCard>
            <VerbaScholaCard>
                <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.75 } }}>
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Invitation link</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 12.5, lineHeight: 1.5, color: SLATE, mt: 0.5 }}>
                            Use email invitations so access stays connected to the intended teacher.
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1.25, borderRadius: '11px', backgroundColor: SURFACE_MUTED, overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Private per teacher invitation</Typography>
                    </Box>
                    <Button fullWidth disabled startIcon={<ContentCopyRoundedIcon />} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                        Copy invitation link
                    </Button>
                </Stack>
            </VerbaScholaCard>
        </Stack>
    );
}

export default function VerbaScholaTeacherCourseSettingsPage({
    course,
    teacherList,
    saving,
    studentSignupUrl,
    onNamePreview,
    onSave,
    onCopyStudentSignup,
    onInviteTeacher,
    onUpdateTeacher,
    onRemoveTeacher,
}: {
    course: IVerbaScholaCourse;
    teacherList: IVerbaScholaTeacherCourse[];
    saving: boolean;
    studentSignupUrl: string | null;
    onNamePreview: (name: string) => void;
    onSave: (payload: Partial<IVerbaScholaCourse>) => void;
    onCopyStudentSignup: () => void;
    onInviteTeacher: (teacher: { first_name: string; last_name: string; email: string; confirm_teacher_role_grant: boolean }) => Promise<boolean>;
    onUpdateTeacher: (teacherCourseId: number, teacher: { first_name: string; last_name: string; email: string }) => Promise<boolean>;
    onRemoveTeacher: (teacherCourseId: number) => void;
}) {
    const [name, setName] = React.useState(course.name);
    const [courseNumber, setCourseNumber] = React.useState(textValue(course.course_number));
    const [description, setDescription] = React.useState(textValue(course.description));
    const [semester, setSemester] = React.useState(textValue(course.semester));
    const [meetingDays, setMeetingDays] = React.useState<string[]>(normalizeMeetingDays(course.meeting_days));
    const initialStartTime = startTimeParts(course.start_time);
    const [startHour, setStartHour] = React.useState(initialStartTime.hour);
    const [startMinute, setStartMinute] = React.useState(initialStartTime.minute);
    const [startPeriod, setStartPeriod] = React.useState<TimePeriod>(initialStartTime.period);
    const [room, setRoom] = React.useState(textValue(course.room));
    const [isArchived, setIsArchived] = React.useState(course.is_archived);
    const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);
    const [teacherDialogMode, setTeacherDialogMode] = React.useState<'invite' | 'edit' | null>(null);
    const [teacherEditTarget, setTeacherEditTarget] = React.useState<IVerbaScholaTeacherCourse | null>(null);
    const [teacherRemoveOpen, setTeacherRemoveOpen] = React.useState(false);
    const [teacherForm, setTeacherForm] = React.useState(EMPTY_TEACHER_FORM);
    const [confirmTeacherRoleGrant, setConfirmTeacherRoleGrant] = React.useState(false);
    const teacherIdentityLocked =
        teacherDialogMode === 'edit' &&
        teacherEditTarget?.status === 'accepted' &&
        teacherEditTarget?.has_joined !== false;
    const teacherEmailChanged =
        teacherDialogMode === 'edit' &&
        !teacherIdentityLocked &&
        teacherForm.email.trim().toLowerCase() !== (teacherEditTarget?.email ?? '').trim().toLowerCase();
    const teacherFormComplete =
        teacherForm.firstName.trim().length > 0 &&
        teacherForm.lastName.trim().length > 0 &&
        teacherForm.email.trim().length > 0 &&
        (teacherDialogMode === 'edit' || confirmTeacherRoleGrant);
    const openInviteTeacherDialog = () => {
        setTeacherEditTarget(null);
        setTeacherForm(EMPTY_TEACHER_FORM);
        setConfirmTeacherRoleGrant(false);
        setTeacherDialogMode('invite');
    };
    const openEditTeacherDialog = (teacher: IVerbaScholaTeacherCourse) => {
        setTeacherEditTarget(teacher);
        setTeacherForm(teacherFormFor(teacher));
        setConfirmTeacherRoleGrant(false);
        setTeacherDialogMode('edit');
    };
    const closeInviteDialog = () => {
        if (saving) return;
        setTeacherForm(EMPTY_TEACHER_FORM);
        setConfirmTeacherRoleGrant(false);
        setTeacherEditTarget(null);
        setTeacherDialogMode(null);
    };
    const submitTeacherInvite = () => {
        if (saving || teacherIdentityLocked || !teacherFormComplete) return;
        const firstName = teacherForm.firstName.trim();
        const lastName = teacherForm.lastName.trim();
        const email = teacherForm.email.trim();
        const action = teacherDialogMode === 'edit' && teacherEditTarget
            ? onUpdateTeacher(teacherEditTarget.id, { first_name: firstName, last_name: lastName, email })
            : onInviteTeacher({ first_name: firstName, last_name: lastName, email, confirm_teacher_role_grant: true });
        action.then(success => {
            if (!success) return;
            setTeacherForm(EMPTY_TEACHER_FORM);
            setConfirmTeacherRoleGrant(false);
            setTeacherEditTarget(null);
            setTeacherDialogMode(null);
        });
    };

    React.useEffect(() => {
        setName(course.name);
        onNamePreview(course.name);
        setCourseNumber(textValue(course.course_number));
        setDescription(textValue(course.description));
        setSemester(textValue(course.semester));
        setMeetingDays(normalizeMeetingDays(course.meeting_days));
        const nextStartTime = startTimeParts(course.start_time);
        setStartHour(nextStartTime.hour);
        setStartMinute(nextStartTime.minute);
        setStartPeriod(nextStartTime.period);
        setRoom(textValue(course.room));
        setIsArchived(course.is_archived);
    }, [course, onNamePreview]);

    const savePayload = () => onSave({
        name: name.trim(),
        course_number: optionalText(courseNumber),
        description: optionalText(description),
        semester: optionalText(semester),
        meeting_days: meetingDays.length ? orderedMeetingDays(meetingDays) : null,
        start_time: startTimeValue(startHour, startMinute, startPeriod),
        room: optionalText(room),
        is_archived: isArchived,
    });
    return (
        <Stack spacing={2.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(280px, .65fr)' }, gap: 2.5 }}>
                <VerbaScholaCard>
                    <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.75 } }}>
                        <Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Class details</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE, mt: 0.25 }}>
                                Information students see when they join the class.
                            </Typography>
                        </Box>
                        <TextField
                            label="Class name"
                            value={name}
                            onChange={event => {
                                setName(event.target.value);
                                onNamePreview(event.target.value);
                            }}
                            fullWidth
                        />
                        <TextField label="Description" value={description} onChange={event => setDescription(event.target.value)} fullWidth multiline minRows={3} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                            <TextField label="Course number" value={courseNumber} onChange={event => setCourseNumber(event.target.value)} fullWidth />
                            <TextField label="Semester" value={semester} onChange={event => setSemester(event.target.value)} fullWidth />
                            <TextField label="Room" value={room} onChange={event => setRoom(event.target.value)} fullWidth />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 112px 112px 112px' }, gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="course-meeting-days-label">Meeting days</InputLabel>
                                <Select
                                    labelId="course-meeting-days-label"
                                    multiple
                                    value={meetingDays}
                                    label="Meeting days"
                                    renderValue={selected => meetingDaysLabel(selected as string[], 'label') ?? ''}
                                    onChange={event => {
                                        const value = event.target.value as string[] | string;
                                        setMeetingDays(typeof value === 'string' ? value.split(',') : value);
                                    }}
                                >
                                    {WEEKDAY_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Checkbox checked={meetingDays.includes(option.value)} />
                                            <ListItemText primary={option.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="course-start-hour-label">Hour</InputLabel>
                                <Select labelId="course-start-hour-label" value={startHour} label="Hour" onChange={event => setStartHour(event.target.value)}>
                                    <MenuItem value="">None</MenuItem>
                                    {TIME_HOUR_OPTIONS.map(hour => <MenuItem key={hour} value={hour}>{hour}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="course-start-minute-label">Minute</InputLabel>
                                <Select labelId="course-start-minute-label" value={startMinute} label="Minute" onChange={event => setStartMinute(event.target.value)}>
                                    {TIME_MINUTE_OPTIONS.map(minute => <MenuItem key={minute} value={minute}>{minute}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="course-start-period-label">AM/PM</InputLabel>
                                <Select labelId="course-start-period-label" value={startPeriod} label="AM/PM" onChange={event => setStartPeriod(event.target.value as TimePeriod)}>
                                    <MenuItem value="AM">AM</MenuItem>
                                    <MenuItem value="PM">PM</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={isArchived} onChange={event => setIsArchived(event.target.checked)} />}
                            label="Archived"
                            sx={{ '& .MuiFormControlLabel-label': { fontFamily: INTER } }}
                        />
                        <Button
                            variant="contained"
                            disabled={saving || name.trim().length === 0}
                            onClick={() => isArchived && !course.is_archived ? setArchiveConfirmOpen(true) : savePayload()}
                            sx={{ ...primaryButtonSx, alignSelf: 'flex-start', borderRadius: '11px', px: 3, textTransform: 'none' }}
                        >
                            Save changes
                        </Button>
                    </Stack>
                </VerbaScholaCard>

                <Stack spacing={2.5}>
                    <StudentSignupCard signupUrl={studentSignupUrl} onCopy={onCopyStudentSignup} />
                </Stack>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(280px, .65fr)' }, gap: 2.5 }}>
                <VerbaScholaCard>
                    <VerbaScholaCardHeader
                        title="Teachers"
                        meta="Manage who can edit lessons, students, and speaking feedback for this class."
                        actions={
                            <Button
                                variant="contained"
                                startIcon={<PersonAddAltRoundedIcon />}
                                disabled={saving}
                                onClick={openInviteTeacherDialog}
                                sx={{ ...primaryButtonSx, borderRadius: '11px', textTransform: 'none' }}
                            >
                                Invite teacher
                            </Button>
                        }
                    />
                    {teacherList.length === 0 ? (
                        <VerbaScholaEmptyState message="No teachers are attached to this class." />
                    ) : (
                        <Stack divider={<Box sx={{ height: '1px', backgroundColor: BORDER }} />}>
                            {teacherList.map(teacher => {
                                const isOwner = teacher.teacher_user_id === course.owner_teacher_id;
                                const name = teacher.display_name ?? teacher.email ?? 'Teacher name unavailable';
                                return (
                                    <Stack key={teacher.id} direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2, sm: 2.75 }, py: 1.75 }}>
                                        <TeacherAvatar teacher={teacher} isOwner={isOwner} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap flexWrap="wrap">
                                                <Typography noWrap sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13.5, color: NAVY }}>{name}</Typography>
                                                {isOwner && <Chip label="Owner" size="small" sx={{ height: 21, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />}
                                            </Stack>
                                            <Typography noWrap sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>
                                                {teacher.email ?? 'Email unavailable'}
                                            </Typography>
                                        </Box>
                                        <Chip label={teacherStatusLabel(teacher)} size="small" sx={statusChipSx(teacher.has_joined === false ? 'pending' : teacher.status)} />
                                        {!isOwner && (
                                            <Tooltip title="Edit teacher">
                                                <IconButton
                                                    aria-label={`Edit ${name}`}
                                                    disabled={saving}
                                                    onClick={() => openEditTeacherDialog(teacher)}
                                                    sx={{ color: SLATE, '&:hover': { color: BLUE } }}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </VerbaScholaCard>

                <TeacherAccessCards />
            </Box>

            <Dialog open={teacherDialogMode !== null} onClose={closeInviteDialog} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>
                    {teacherDialogMode === 'edit' ? 'Edit teacher' : 'Invite a teacher'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE, mb: 2 }}>
                        {teacherIdentityLocked
                            ? 'This teacher has signed in and now manages their own name and email.'
                            : teacherDialogMode === 'edit'
                              ? 'They have not signed in yet, so you can still correct their name or email.'
                              : 'They are added as a teacher right away and emailed a link to set up their account if needed.'}
                    </Typography>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="First name"
                                value={teacherForm.firstName}
                                onChange={event => setTeacherForm(current => ({ ...current, firstName: event.target.value }))}
                                disabled={saving || teacherIdentityLocked}
                            />
                            <TextField
                                fullWidth
                                label="Last name"
                                value={teacherForm.lastName}
                                onChange={event => setTeacherForm(current => ({ ...current, lastName: event.target.value }))}
                                disabled={saving || teacherIdentityLocked}
                            />
                        </Stack>
                        <TextField
                            fullWidth
                            label="Teacher email"
                            type="email"
                            value={teacherForm.email}
                            onChange={event => setTeacherForm(current => ({ ...current, email: event.target.value }))}
                            onKeyDown={event => {
                                if (event.key === 'Enter' && !teacherIdentityLocked) {
                                    submitTeacherInvite();
                                }
                            }}
                            disabled={saving || teacherIdentityLocked}
                            helperText={
                                teacherIdentityLocked
                                    ? 'Only the teacher can change this.'
                                    : teacherEmailChanged
                                      ? 'Saving sends a new invitation to this email.'
                                      : 'The teacher signs in with this address.'
                            }
                        />
                        {teacherDialogMode === 'invite' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={confirmTeacherRoleGrant}
                                        onChange={event => setConfirmTeacherRoleGrant(event.target.checked)}
                                        disabled={saving}
                                    />
                                }
                                label="I understand this grants teacher access if this email belongs to an existing learner account."
                                sx={{
                                    alignItems: 'flex-start',
                                    '& .MuiFormControlLabel-label': {
                                        fontFamily: INTER,
                                        fontSize: 12.5,
                                        lineHeight: 1.45,
                                        color: SLATE,
                                        pt: 0.8,
                                    },
                                }}
                            />
                        )}
                        {saving && !teacherIdentityLocked && (
                            <Stack direction="row" alignItems="center" spacing={1} role="status" aria-live="polite">
                                <CircularProgress size={16} aria-label="Saving teacher details" sx={{ color: BLUE }} />
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>
                                    {teacherDialogMode === 'edit' ? 'Saving teacher details...' : 'Inviting teacher and sending invitation...'}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {teacherDialogMode === 'edit' && (
                        <Button
                            disabled={saving}
                            onClick={() => setTeacherRemoveOpen(true)}
                            sx={{ mr: 'auto', fontFamily: INTER, fontWeight: 700, color: ERROR_TEXT, textTransform: 'none', '&:hover': { backgroundColor: ERROR_SOFT } }}
                        >
                            Remove access
                        </Button>
                    )}
                    <Button disabled={saving && !teacherIdentityLocked} onClick={closeInviteDialog} sx={{ color: SLATE, fontFamily: INTER, textTransform: 'none' }}>
                        {teacherIdentityLocked ? 'Close' : 'Cancel'}
                    </Button>
                    {!teacherIdentityLocked && (
                        <Button
                            variant="contained"
                            disabled={saving || !teacherFormComplete}
                            onClick={submitTeacherInvite}
                            sx={{ ...primaryButtonSx, px: 2.5, textTransform: 'none' }}
                        >
                            {saving && <CircularProgress size={18} aria-label="Saving teacher" color="inherit" sx={{ mr: 1 }} />}
                            {saving ? (teacherDialogMode === 'edit' ? 'Saving...' : 'Sending...') : (teacherDialogMode === 'edit' ? 'Save changes' : 'Send invitation')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={archiveConfirmOpen} onClose={() => setArchiveConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Archive this class?</DialogTitle>
                <DialogContent><Typography sx={{ fontFamily: INTER, color: SLATE }}>Archiving removes the class from active work. You can restore it later from class settings.</Typography></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setArchiveConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => { setArchiveConfirmOpen(false); savePayload(); }} sx={{ backgroundColor: ERROR_TEXT, color: '#fff', '&:hover': { backgroundColor: ERROR_TEXT, filter: 'brightness(0.9)' } }}>Archive class</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={teacherRemoveOpen} onClose={() => setTeacherRemoveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>
                    Remove {teacherEditTarget?.display_name ?? 'this teacher'} from this class?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE }}>
                        They lose teacher access to this class. They keep their account and any access they have to other classes.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setTeacherRemoveOpen(false)} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={saving || teacherEditTarget == null}
                        onClick={() => {
                            if (!teacherEditTarget) return;
                            setTeacherRemoveOpen(false);
                            closeInviteDialog();
                            onRemoveTeacher(teacherEditTarget.id);
                        }}
                        sx={{ fontFamily: INTER, fontWeight: 700, px: 2.5, textTransform: 'none', backgroundColor: ERROR_TEXT, color: '#fff', boxShadow: 'none', '&:hover': { backgroundColor: ERROR_TEXT, filter: 'brightness(0.9)', boxShadow: 'none' } }}
                    >
                        Remove teacher
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
